import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Breadcrumbs, Container, Tab, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { LoadingIndicator } from 'src/common';
import { QueryField } from 'src/store';
import {
  REPORTING_CURRENT_TAB_KEY,
  REPORTING_DETAILS_KEY,
  REPORTING_EMAIL_TEMPLATE_KEY,
  REPORTING_LOGS_KEY,
  REPORTING_REPORTS_KEY,
} from '../../constants';
import { Props } from '../container';
import { ReportingBatchParams } from '../store/types';
import { ReportingBatchDetails } from './ReportingBatchDetails';
import { ReportingBatchLogs } from './ReportingBatchLogs';
import { ReportingBatchRecords } from './ReportingBatchRecords';
import { ReportingEmailTemplate } from './ReportingEmailTemplate';

export const ReportingBatchComponent = (props: Props) => {
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const { search } = useLocation();

  const { id } = useParams<ReportingBatchParams>();

  const history = useHistory();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const currentTab = searchParams.get(REPORTING_CURRENT_TAB_KEY);

  const changeTab = (value: string) => {
    searchParams.set(REPORTING_CURRENT_TAB_KEY, value);
    history.replace({ search: searchParams.toString() });
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => changeTab(newValue);

  const fetchBulkReportLogList = (page: number, pageSize: number) => {
    if (!id) return;
    props.fetchBulkReportLogList({ batchId: id, page: page, pageSize: pageSize });
  };

  const fetchBulkReportRecords = (page: number, pageSize: number, queryFields: QueryField[] = []) => {
    if (!id) return;
    props.fetchBulkReportRecordList({ batchId: id, page: page, pageSize: pageSize, queryFields: queryFields });
  };

  const fetchBulkReport = () => {
    if (!id) return;
    props.fetchBulkReport(id);
  };

  const fetchBulkProgress = () => {
    if (!id) return;
    props.fetchBulkProgress(id);
  };

  const downloadCsvForBatchRecords = (selectedRows: number[]) => {
    if (!id) return;
    props.downloadBatchRecordsCsv({ batchId: id, selectedRows: selectedRows });
  };

  const downloadCsvForBatchLogs = (selectedLogIds: string[]) => {
    if (!id) return;
    props.downloadBatchLogsCsv({ batchId: id, selectedLogIds: selectedLogIds });
  };

  const approveScheduledInstanceEvent = (isApproved: boolean, isRejected: boolean, isRetry: boolean) => {
    if (!id) return;
    props
      .approveScheduledInstance({
        batchId: id,
        isApproved: isApproved,
        isRejected: isRejected,
        isRetry: isRetry,
      })
      .unwrap()
      .then(() => {
        fetchBulkProgress();
        fetchBulkReport();
      });
  };

  const terminateRecord = (currentPage: number, currentPageSize: number, recordIndex?: number) => {
    if (!id) return;
    if (recordIndex) {
      props.terminateBulkRequest({ batchId: id, allRecords: false, batchRecords: [recordIndex] });
    } else {
      props.terminateBulkRequest({ batchId: id, allRecords: true });
    }

    if (currentPage > -1) {
      setRefreshing(true);
      setTimeout(() => {
        fetchBulkReportRecords(currentPage, currentPageSize);
        setRefreshing(false);
        fetchBulkProgress();
      }, 3000);
    }
  };

  const initiateBulk = (currentPage: number, currentPageSize: number, allRecords: boolean, batchRecords?: number[]) => {
    if (!id) return;
    props.initiateBulkRequest({ batchId: id, allRecords, batchRecords });

    if (currentPage > -1) {
      setRefreshing(true);
      setTimeout(() => {
        fetchBulkReportRecords(currentPage, currentPageSize);
        setRefreshing(false);
        fetchBulkProgress();
      }, 3000);
    }
  };

  useEffect(() => {
    if (!searchParams.get(REPORTING_CURRENT_TAB_KEY)) {
      changeTab(REPORTING_DETAILS_KEY);
    }
  }, []);

  useEffect(() => {
    fetchBulkReport();
    fetchBulkProgress();
  }, []);

  if (!id || props.fetchBulkReportProgress.hasErrors) {
    return <Redirect to="/reporting" />;
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <LoadingIndicator progress={props.fetchBulkReportProgress}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Typography onClick={() => history.push('/reporting?tab=library')} sx={{ ':hover': { cursor: 'pointer' } }}>
            Reporting Library
          </Typography>
          <Typography>{id}</Typography>
        </Breadcrumbs>

        <TabContext value={currentTab || REPORTING_DETAILS_KEY}>
          <TabList TabIndicatorProps={{ style: { height: '4px' } }} onChange={handleChange}>
            <Tab value={REPORTING_DETAILS_KEY} label="Details" />
            {props.batch.bulkReport.scheduleInstanceDetails && props.batch.bulkReport.emailParameters && (
              <Tab value={REPORTING_EMAIL_TEMPLATE_KEY} label="Email Template" />
            )}
            <Tab value={REPORTING_REPORTS_KEY} label="Reports" />
            {props.hasClientEditAdminOnly && <Tab value={REPORTING_LOGS_KEY} label="Logs" />}
          </TabList>

          <TabPanel value={REPORTING_DETAILS_KEY}>
            <ReportingBatchDetails {...props} changeTab={changeTab} initiateBulk={initiateBulk} />
          </TabPanel>
          {props.batch.bulkReport.scheduleInstanceDetails && props.batch.bulkReport.emailParameters && (
            <TabPanel value={REPORTING_EMAIL_TEMPLATE_KEY}>
              <ReportingEmailTemplate {...props} />
            </TabPanel>
          )}
          <TabPanel value={REPORTING_REPORTS_KEY}>
            <ReportingBatchRecords
              {...props}
              downloadCsvForBatchRecords={downloadCsvForBatchRecords}
              getBulkProgress={fetchBulkProgress}
              fetchBulkReportRecords={fetchBulkReportRecords}
              approveScheduledInstanceEvent={approveScheduledInstanceEvent}
              refetchBulkReport={fetchBulkReport}
              initiateBulk={initiateBulk}
              terminateRecord={terminateRecord}
              refreshing={refreshing}
            />
          </TabPanel>

          {props.hasClientEditAdminOnly && (
            <TabPanel value={REPORTING_LOGS_KEY}>
              <ReportingBatchLogs
                batch={props.batch}
                fetchBulkReportLogListProgress={props.fetchBulkReportLogListProgress}
                fetchBulkReportLogList={fetchBulkReportLogList}
                downloadCsvForBatchLogs={downloadCsvForBatchLogs}
              />
            </TabPanel>
          )}
        </TabContext>
      </LoadingIndicator>
    </Container>
  );
};
